import { getLovLabel } from '@zoryn/hooks'
import { isActiveFilter, isValuelessOperator } from './filter-utils'
import type { GridColumnInfo, GridFilter, GridFilterGroupState } from '../types'

export interface FilterDisplayCondition {
  id: string
  field: string
  columnTitle: string
  operator: string
  operatorLabel: string
  valueLabel: string
  logic: string
}

export interface FilterDisplayGroup {
  id: string
  logic: 'and' | 'or'
  groupLogic: 'and' | 'or'
  conditions: FilterDisplayCondition[]
}

function resolveColumnTitle(columns: GridColumnInfo[], field: string) {
  return columns.find((col) => col.field === field)?.title || field
}

function resolveColumn(columns: GridColumnInfo[], field: string) {
  return columns.find((col) => col.field === field)
}

function formatOperatorLabel(operator: string, t: (key: string) => string) {
  const key = `z.table.operator.${operator}`
  const label = t(key)
  return label === key ? operator : label
}

function formatValueLabel(
  cond: GridFilter,
  column: GridColumnInfo | undefined
) {
  if (isValuelessOperator(cond.operator)) {
    return ''
  }
  const value = cond.value
  if (value === null || value === undefined || value === '') {
    return ''
  }
  if (column?.lovType) {
    return getLovLabel(column.lovType, value) || String(value)
  }
  return String(value)
}

function formatConditionText(cond: FilterDisplayCondition) {
  if (cond.valueLabel) {
    return `${cond.columnTitle} ${cond.operatorLabel} ${cond.valueLabel}`
  }
  return `${cond.columnTitle} ${cond.operatorLabel}`
}

export function buildFilterDisplayGroups(
  groups: GridFilterGroupState[],
  columns: GridColumnInfo[],
  t: (key: string) => string
): FilterDisplayGroup[] {
  return groups
    .map((group) => ({
      id: group.id,
      logic: (group.logic || 'and') as 'and' | 'or',
      groupLogic: (group.groupLogic || 'or') as 'and' | 'or',
      conditions: group.conditions
        .filter(isActiveFilter)
        .map((cond) => {
          const column = resolveColumn(columns, cond.field)
          return {
            id: cond.id,
            field: cond.field,
            columnTitle: resolveColumnTitle(columns, cond.field),
            operator: cond.operator,
            operatorLabel: formatOperatorLabel(cond.operator, t),
            valueLabel: formatValueLabel(cond, column),
            logic: cond.logic || 'and',
          }
        }),
    }))
    .filter((group) => group.conditions.length > 0)
}

export function formatFilterDisplayGroups(
  groups: FilterDisplayGroup[],
  t: (key: string) => string
) {
  return groups
    .map((group, groupIndex) => {
      const inner = group.conditions
        .map((cond, condIndex) => {
          const prefix =
            condIndex > 0
              ? ` ${t(`z.table.logic.${cond.logic}`)} `
              : ''
          return `${prefix}${formatConditionText(cond)}`
        })
        .join('')
      const wrapped = group.conditions.length > 1 ? `(${inner})` : inner
      if (groupIndex === 0) {
        return wrapped
      }
      return ` ${t(`z.table.logic.${group.groupLogic}`)} ${wrapped}`
    })
    .join('')
}

export function removeFilterConditionById(
  groups: GridFilterGroupState[] | null | undefined,
  groupId: string,
  conditionId: string
) {
  if (!Array.isArray(groups)) {
    return []
  }
  for (const group of groups) {
    if (group.id !== groupId) {
      continue
    }
    group.conditions = group.conditions.filter((cond) => cond.id !== conditionId)
    break
  }
  return groups.filter((group) => group.conditions.length > 0)
}
